import React, {
  useState,
  useRef,
  createContext,
  useMemo,
  useEffect
} from 'react';
import { XUserInteraction } from '@core/utils';

// components
import { DataPopup } from '../DataPopup/DataPopup';

// hooks
import useCloseAttempt from './hooks/useCloseAttempt';

// i18n
import { useTranslation } from '@core/hooks';

export { default as useCloseAttempt } from './hooks/useCloseAttempt';

export const PopupContentContext = createContext({});

export const withPopupContent = (
  WrappedComponent,
  PopupContent,
  showCloseButton = true,
  options = {},
  { askBeforeClose, askTxt, initSafeCancel } = {
    askBeforeClose: false
  },
  useRenderFunction = false
) => {
  const initSafeCancelEnabled =
    initSafeCancel === undefined ? true : initSafeCancel;

  const _withPopupContent = (props) => {
    const customReference = useRef(null);

    const [showPopup, setShowPopup] = useState(false);
    const [closeAttemptFired, setCloseAttemptFired] = useState({
      fired: false,
      supress: false
    });
    const [shouldSafeCancel, setShouldSafeCancel] = useState(
      initSafeCancelEnabled
    );
    const [title, setTitle] = useState(null);
    const [currentRowData, setCurrentRowData] = useState({});
    const [customData, setCustomData] = useState({});
    const [popupContentReady, setPopupContentReady] = useState(false);
    const [cleanGridSelectedRows, setCleanGridSelectedRows] = useState(false);

    const { t } = useTranslation();

    const tlAskTxt = useMemo(() => {
      if (askTxt) return t(askTxt);

      return t('Zamknij_confirm');
    }, []);

    const onHidden = () => {
      if (!showPopup) {
        setCloseAttemptFired({ fired: false, supress: false });
        setShouldSafeCancel(initSafeCancelEnabled);
      }

      if (!showPopup) {
        setPopupContentReady(false);
      }
    };

    const onFocusedRowChanged = (e) => {
      if (e.row) {
        setCurrentRowData(e.row.data);
      }
    };

    const customCloseBtnHandler = () =>
      setCloseAttemptFired({ fired: true, supress: false });

    const denyCloseAttempt = () =>
      setCloseAttemptFired({ fired: false, supress: false });

    const handleClosePromise = (callback) => {
      setCloseAttemptFired({ fired: true, supress: true });

      return new Promise((res) => {
        callback(res);
      }).then(denyCloseAttempt);
    };

    useEffect(() => {
      if (
        closeAttemptFired.fired &&
        !closeAttemptFired.supress &&
        !shouldSafeCancel
      ) {
        setShowPopup(false);
      }
    }, [closeAttemptFired, shouldSafeCancel]);

    useCloseAttempt(
      closeAttemptFired,
      denyCloseAttempt,
      setShowPopup,
      tlAskTxt,
      shouldSafeCancel
    );

    const handlePopupCancel = () => {
      handleClosePromise((resolveClosePromise) => {
        if (shouldSafeCancel) {
          XUserInteraction.confirmDialog(
            tlAskTxt,
            () => {
              setShowPopup(false);
              resolveClosePromise();
            },
            resolveClosePromise
          );
        } else {
          setShowPopup(false);
          resolveClosePromise();
        }
      });
    };

    const renderDataPopup = () => (
      <DataPopup
        visibiblity={showPopup}
        setVisibility={setShowPopup}
        customCloseBtnHandler={
          !askBeforeClose || closeAttemptFired.fired
            ? null
            : customCloseBtnHandler
        }
        title={title}
        showCloseButton={showCloseButton}
        onShown={() => setPopupContentReady(true)}
        onHidden={onHidden}
        {...options}
        currentRowData={currentRowData}
      >
        <PopupContentContext.Provider value={{ ready: popupContentReady }}>
          <PopupContent
            customReference={customReference}
            data={currentRowData}
            ready={popupContentReady}
            setShowPopup={setShowPopup}
            shouldSafeCancel={shouldSafeCancel}
            setShouldSafeCancel={setShouldSafeCancel}
            handlePopupCancel={handlePopupCancel}
            setPopupTitle={setTitle}
            customData={customData}
            setCustomData={setCustomData}
            closeAttemptFired={closeAttemptFired}
            denyCloseAttempt={denyCloseAttempt}
            handleClosePromise={askBeforeClose ? handleClosePromise : undefined}
            setCleanGridSelectedRows={setCleanGridSelectedRows}
            cleanGridSelectedRows={cleanGridSelectedRows}
          />
        </PopupContentContext.Provider>
      </DataPopup>
    );

    return (
      <>
        {useRenderFunction ? null : renderDataPopup()}
        <WrappedComponent
          customReference={customReference}
          currentRowData={currentRowData}
          setCurrentRowData={setCurrentRowData}
          onFocusedRowChanged={onFocusedRowChanged}
          showPopup={showPopup}
          setShowPopup={setShowPopup}
          customData={customData}
          setCustomData={setCustomData}
          popupContent={useRenderFunction ? renderDataPopup() : null}
          setCleanGridSelectedRows={setCleanGridSelectedRows}
          cleanGridSelectedRows={cleanGridSelectedRows}
          {...props}
        />
      </>
    );
  };

  return _withPopupContent;
};
